import { DatePicker, DatePickerProps } from "@mui/lab";
import {
  Box,
  FormHelperText,
  IconButton,
  InputLabel,
  PopperProps,
  useTheme,
} from "@mui/material";
import { Calendar, Lock } from "lucide-react";
import { useState } from "react";
import useLocales from "src/hooks/useLocales";
import ChevronLeftIcon from "src/assets/icon/chevron/left";
import ChevronRightIcon from "src/assets/icon/chevron/right";
import { BootstrapInput } from "../form/TextField";

type DatePickerProps_ = {
  label?: string;
  value?: any;
  onChange(date: any, keyboardInputValue?: string | undefined): void;
  error?: boolean | undefined;
  errorMessage?: string;
  disabled?: boolean;
  sx?: any;
  popperPlacement?: PopperProps["placement"];
};
const MyDatePicker = ({
  label,
  value,
  onChange,
  disabled,
  error,
  errorMessage,
  sx,
  popperPlacement,
  ...props
}: DatePickerProps_ & Omit<DatePickerProps, "renderInput">) => {
  const theme = useTheme();
  const { palette } = theme;
  const { translate } = useLocales();
  const [invalidDateMessage, setInvalidDateMessage] = useState("");

  const handleChange = (date: any, keyboardInputValue?: string) => {
    const isInvalid = !!date && isNaN(new Date(date).getTime());
    setInvalidDateMessage(
      isInvalid ? translate("finance_screen.invalid_date_format") : "",
    );
    // Invalid (partially typed) dates are not forwarded, so consumers never receive them
    if (isInvalid) return;
    onChange(date, keyboardInputValue);
  };

  const displayMessage = errorMessage || invalidDateMessage;
  const hasError = error || !!invalidDateMessage;
  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          alignItems: "flex-start",
          p: 0,
          gap: "4px",
        }}
      >
        {label && (
          <InputLabel
            htmlFor={"country-select-demo"}
            sx={{
              fontSize: "12px",
              fontWeight: 700,
              // transform: "translate(0, 1.5px) scale(0.75)",
              color: theme.palette.action.active,
            }}
          >
            {label}
          </InputLabel>
        )}
        <DatePicker
          label={label}
          value={value}
          disabled={disabled}
          onChange={handleChange}
          PopperProps={{
            placement: popperPlacement ?? "top-start",
            modifiers: [
              {
                name: "flip",
                enabled: false,
              },
            ],
          }}
          InputAdornmentProps={{
            sx: {
              position: "absolute",
              right: 0,
              padding: "9px",
            },
          }}
          components={{
            OpenPickerIcon: ({ ...props }) => (
              <div>
                {disabled ? (
                  <Lock strokeWidth="1.5px" size="18px" />
                ) : (
                  <Calendar strokeWidth="1.5px" size="18px" />
                )}
              </div>
            ),
            LeftArrowButton: ({ ...props }) => (
              <IconButton
                size="small"
                sx={{
                  height: "30px",
                  width: "30px",
                  color: palette?.grey["400"],
                  cursor: "pointer",
                  "&:hover": { color: "black" },
                  "&:disabled": {
                    color: palette?.grey["400"],
                    bgcolor: palette?.grey["300"],
                  },
                }}
                {...props}
              >
                <ChevronLeftIcon height={20} width={20} />
              </IconButton>
            ),
            RightArrowButton: ({ ...props }) => (
              <IconButton
                size="small"
                sx={{
                  height: "30px",
                  width: "30px",
                  color: palette?.grey["400"],
                  cursor: "pointer",
                  "&:hover": { color: "black" },
                  "&:disabled": {
                    color: palette?.grey["400"],
                    bgcolor: palette?.grey["300"],
                  },
                }}
                {...props}
              >
                <ChevronRightIcon height={20} width={20} />
              </IconButton>
            ),
          }}
          renderInput={(params_) => {
            let {
              margin,
              onKeyDown,
              onKeyUp,
              InputProps,
              placeholder,
              ...params
            } = params_;
            return (
              <BootstrapInput
                endAdornment={InputProps?.endAdornment}
                {...params}
                ref={params.inputRef}
                placeholder={placeholder}
                fullWidth
                error={hasError}
                sx={{
                  "& .MuiInputBase-input": {
                    ...(disabled && {
                      backgroundColor: theme.palette.text.borderLine,
                      color: theme.palette.text.primary,
                      WebkitTextFillColor: theme.palette.text.primary,
                      opacity: 1,
                      cursor: "not-allowed",
                      mt: 0.7,
                    }),
                    ...(hasError && {
                      backgroundColor: theme.palette.error.light,
                      borderColor: theme.palette.error.dark,
                      color: theme.palette.error.dark,
                      "&::placeholder": {
                        opacity: 1,
                        color: theme.palette.error.dark,
                      },
                      "&:focus": {
                        borderColor: theme.palette.error.dark,
                        boxShadow: `0 0 0 0.01rem ${theme.palette.error.dark}`,
                      },
                    }),
                  },
                }}
                {...params}
              />
            );
          }}
          {...props}
        />
        {displayMessage && (
          <FormHelperText error sx={{ mx: 0, fontSize: "10px" }}>
            {displayMessage}
          </FormHelperText>
        )}
      </Box>
    </>
  );
};

export default MyDatePicker;
